/* eslint-disable @typescript-eslint/no-explicit-any */
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { Status } from "@iventis/domain-model/model/status";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { FeatureCollection, Point, LineString, Polygon, Feature } from "geojson";
import { v4 as uuid } from "uuid";
import { DataFieldValueTypes } from "@iventis/types";
import area from "@turf/area";
import length from "@turf/length";
import {
    coordinatesToString,
    decimalAdjust,
    isValidDate,
    isValidTimeRange as isValidTimeRangeString,
    isValidTimeSpan,
    parseRepeatedTimeRangeDataFieldValueToNumber,
} from "@iventis/utilities";
import { RepeatedTimeRange } from "@iventis/domain-model/model/repeatedTimeRange";
import {
    getMapObjectNameSystemDatafield,
    getMapObjectAreaSystemDataField,
    getMapObjectLengthSystemDataField,
    getMapObjectQuantityOfModelsOnLineSystemDataField,
    getQuantityOfModelsOnALine,
    getMapObjectCoordinatesSystemDataField,
} from "@iventis/datafields";
import { PostImportedGeoJsonMapObject, SupportedGeojsonPropertyTypes } from "./layer-geojson-types";

/**
 * Will create a new map object for each feature in feature collection
 *
 * Attributes values will be taken from the feature collection, area and length attribute will be calculated where appropriate
 */
export async function createLayerObjectsFromGeoJson(
    mapLayer: MapLayer,
    attributeListItems: { [attributeId: string]: DataFieldListItem[] },
    importedGeoJsonFeatureCollection: FeatureCollection<Point | LineString | Polygon>,
    mappedAttributes: { [attributeId: string]: string },
    projectAttributes: DataField[],
    postMapObjects: (mapObjects: PostImportedGeoJsonMapObject[]) => Promise<void>
) {
    // Create attribute map, reduces the amount of cycling through arrays
    const layerAttributeMap = mapLayer.dataFields.reduce<{ [attributeId: string]: DataField }>((attributesMap, attribute) => ({ ...attributesMap, [attribute.id]: attribute }), {});
    const mapObjects: PostImportedGeoJsonMapObject[] = importedGeoJsonFeatureCollection.features.map((importedFeature) => {
        const attributeValues = createAttributeValues(layerAttributeMap, attributeListItems, mappedAttributes, importedFeature.properties);
        // Get all datafield values, includes SystemDataFields of length and area
        const requiredAttributes = addRequiredAttributes(mapLayer, importedFeature, projectAttributes, attributeValues);

        // Validate the rotation of the object
        const validRotation = createRotationProperty(importedFeature);

        return {
            id: uuid(),
            layerId: mapLayer.id,
            layerName: mapLayer.name,
            status: Status.Active,
            geoJsonFeature: { ...importedFeature, properties: { ...importedFeature.properties, rotation: validRotation } },
            dataFieldValues: { ...attributeValues, ...requiredAttributes },
        };
    });

    await postMapObjects(mapObjects);
}

/** Checks the passed feature for the rotation property and checks it is valid. Returns a blank rotation if it is not valid. */
export function createRotationProperty(feature: Feature<Point | LineString | Polygon>): { x: number; y: number; z: number } {
    if (feature.properties?.rotation != null && typeof feature.properties.rotation === "object") {
        const { rotation } = feature.properties;
        if (typeof rotation.x === "number" && typeof rotation.y === "number" && typeof rotation.z === "number") {
            return rotation;
        }
    }

    return { x: 0, y: 0, z: 0 };
}

/** Creates attributes values for a map object and ensures value is of correct type */
export function createAttributeValues(
    layerAttributeMap: { [attributeId: string]: DataField },
    attributeListItems: { [listItemId: string]: DataFieldListItem[] },
    mappedAttributes: { [attributeId: string]: string },
    importedGeoJsonFeatureProperties: { [name: string]: SupportedGeojsonPropertyTypes }
) {
    const attributeValues: { [dataFieldId: string]: DataFieldValueTypes } = {};
    Object.entries(mappedAttributes).forEach(([attributeId, propertyKey]) => {
        const attribute = layerAttributeMap[attributeId];
        // Get property value from imported feature
        const geoJsonPropertyValue = importedGeoJsonFeatureProperties[propertyKey];
        // Ensure attribute value is correct type
        const attributeValue = parseAttributeValue(attribute, geoJsonPropertyValue, attributeListItems);
        if (attributeValue != null) {
            attributeValues[attribute.id] = attributeValue;
        }
    });
    return attributeValues;
}

/**
 * Ensures attribute values are the correct type, if not correct type will return null
 *
 * List items return a list item id which is matched by the property name
 */
export function parseAttributeValue(attribute: DataField, geoJsonPropertyValue: SupportedGeojsonPropertyTypes, attributeListItems: { [attributeId: string]: DataFieldListItem[] }) {
    switch (true) {
        case attribute.type === DataFieldType.Text && typeof geoJsonPropertyValue === "string":
        case attribute.type === DataFieldType.Hyperlink && typeof geoJsonPropertyValue === "string":
        case attribute.type === DataFieldType.What3Words && typeof geoJsonPropertyValue === "string":
            return geoJsonPropertyValue;
        case attribute.type === DataFieldType.Text && typeof geoJsonPropertyValue === "number":
            return JSON.stringify(geoJsonPropertyValue);
        case attribute.type === DataFieldType.Number && typeof geoJsonPropertyValue === "number":
            return geoJsonPropertyValue;
        case attribute.type === DataFieldType.Date && typeof geoJsonPropertyValue === "string": {
            const date = new Date(geoJsonPropertyValue as string);
            // Check that date is not "Invalid Date" value
            return Number.isNaN(date.getDay()) ? null : date;
        }
        case attribute.type === DataFieldType.Tickbox && typeof geoJsonPropertyValue === "boolean":
            return geoJsonPropertyValue;
        case attribute.type === DataFieldType.List && typeof geoJsonPropertyValue === "string": {
            const listItems = attributeListItems[attribute.id];
            // Find list item with the name is the same as the property value
            const foundListItem = listItems.find((listItem) => listItem.name === geoJsonPropertyValue);
            // If no list item can be found then return null
            return foundListItem != null ? foundListItem.id : null;
        }
        case attribute.type === DataFieldType.Time && typeof geoJsonPropertyValue === "string": {
            return isValidTimeSpan(geoJsonPropertyValue) ? geoJsonPropertyValue : null;
        }
        case attribute.type === DataFieldType.TimeRange && typeof geoJsonPropertyValue === "string": {
            return isValidTimeRangeString(geoJsonPropertyValue) ? geoJsonPropertyValue : null;
        }
        case attribute.type === DataFieldType.RepeatedTimeRanges:
            if (isValidRepeatedTimeRanges(geoJsonPropertyValue)) {
                // Each time range needs a new id
                return parseRepeatedTimeRangeDataFieldValueToNumber(geoJsonPropertyValue.map((timeRange) => ({ id: uuid(), ...timeRange })));
            }
            return null;
        default:
            return null;
    }
}

/** Ensures all required attributes are present including: MapObjectName, Area and Length  */
export function addRequiredAttributes(layer: MapLayer, feature: Feature, projectAttributes: DataField[], attributeValues: { [attributeId: string]: DataFieldValueTypes }) {
    const calculatedAttributes = createCalculatedAttributes(layer, { ...feature, properties: { ...feature.properties, ...attributeValues } }, projectAttributes);
    // Get map object name attribute and
    const mapObjectNameAttribute = getMapObjectNameSystemDatafield(projectAttributes);

    if (mapObjectNameAttribute == null) {
        throw new Error("Could not find map object name attribute");
    }

    // Check if MapObjectName has been set
    if (attributeValues?.[mapObjectNameAttribute.id] == null) {
        // If missing MapObjectName value, give it a default one
        calculatedAttributes[mapObjectNameAttribute.id] = "Map object";
    }
    return calculatedAttributes;
}

/**
 * Find the project attribute for that style type and calculate it's value
 *
 * Line and LineModel need length and Area need area
 */
export function createCalculatedAttributes(layer: MapLayer, feature: Feature, projectAttributes: DataField[]): { [attributeId: string]: DataFieldValueTypes } {
    switch (layer.styleType) {
        case StyleType.Area: {
            const areaAttribute = getMapObjectAreaSystemDataField(projectAttributes);
            if (areaAttribute == null) {
                throw new Error("Area attribute could not be found in project attributes");
            }

            return { [areaAttribute.id]: decimalAdjust("round", area(feature), -2) };
        }
        case StyleType.Line: {
            const lengthAttribute = getMapObjectLengthSystemDataField(projectAttributes);
            if (lengthAttribute == null) {
                throw new Error("Length attribute could not be found in project attributes");
            }

            return { [lengthAttribute.id]: decimalAdjust("round", length(feature, { units: "meters" }), -2) };
        }
        case StyleType.LineModel: {
            const lengthAttribute = getMapObjectLengthSystemDataField(projectAttributes);
            if (lengthAttribute == null) {
                throw new Error("Length attribute could not be found in project attributes");
            }
            const quantityAttribute = getMapObjectQuantityOfModelsOnLineSystemDataField(projectAttributes);
            if (quantityAttribute == null) {
                throw new Error("Quantity attribute could not be found in project attributes");
            }
            return {
                [lengthAttribute.id]: decimalAdjust("round", length(feature, { units: "meters" }), -2),
                [quantityAttribute.id]: getQuantityOfModelsOnALine(feature, layer.lineModelStyle),
            };
        }
        case StyleType.Point:
        case StyleType.Icon:
        case StyleType.Model: {
            const coordinateAttribute = getMapObjectCoordinatesSystemDataField(projectAttributes);
            if (coordinateAttribute == null) {
                throw new Error("Coordinate attribute could not be found in project attributes");
            }

            return { [coordinateAttribute.id]: feature.geometry.type === "Point" ? coordinatesToString(feature.geometry.coordinates) : null };
        }
        default:
            return {};
    }
}

/** Validates an array of RepeatedTimeRanges */
export function isValidRepeatedTimeRanges(timeRanges: unknown): timeRanges is RepeatedTimeRange[] {
    if (!Array.isArray(timeRanges)) {
        return false;
    }

    return timeRanges.every(isValidTimeRange);
}

/** Validates a single RepeatedTimeRange */
export function isValidTimeRange(timeRange: any): timeRange is RepeatedTimeRange {
    if (timeRange == null || typeof timeRange !== "object") {
        return false;
    }

    if (!("name" in timeRange)) {
        return false;
    }

    // Check start and end time are valid time spans
    const validStartTime = "startTime" in timeRange && typeof timeRange.startTime === "string" && isValidTimeSpan(timeRange.startTime);
    const validEndTime = "endTime" in timeRange && typeof timeRange.endTime === "string" && isValidTimeSpan(timeRange.endTime);

    if (!(validStartTime && validEndTime)) {
        return false;
    }

    // Check that start and end date are valid dates
    const validStartDate = "startDate" in timeRange && typeof timeRange.startDate === "string" && isValidDate(new Date(timeRange.startDate));
    const validEndDate = "endDate" in timeRange && typeof timeRange.endDate === "string" && isValidDate(new Date(timeRange.endDate));

    if (!(validStartDate && validEndDate)) {
        return false;
    }

    return true;
}
